import BCMSModalItem from "./src/BCMSModalItem";

/**
 * @class BCMSModalState
 * @classdesc Class definition for BCMSModalState
 * @param {OBJECT} modals - The modals that should be supported
 * 
 * @property {BOOLEAN} _isActive - Determines the state if the overlay should be shown
 * @property {MAP} _modals - A map that stores all key value pairs for supported modals
 */
export default class BCMSModalState {
    _isActive;
    _modals;

    constructor(modals) {
        this.isActive = false;
        this.modals = modals;
    }

    /**
     * @function isActive
     * @instance
     * @param {BOOLEAN} value - Set the isActive property to the passed value
     * @description Setter for the isActive property
     * @memberof BCMSModalState
     */
    set isActive(value) { this._isActive = value; }

    /**
     * @function modals
     * @instance
     * @param {OBJECT} value - Set the modals map based on the passed object
     * @description Setter for the modals property
     * @memberof BCMSModalState
     */
    set modals(value) {
        this._modals = Object.entries(value).reduce((result, [ key, value ]) => {
            result.set(key, new BCMSModalItem(key, value));
            return result;
        }, new Map());
    }

    /**
     * @function isActive
     * @instance
     * @returns {BOOLEAN}
     * @description Getter for the isActive property
     * @memberof BCMSModalState
     */
    get isActive() { return this._isActive; }

    /**
     * @function modals
     * @instance
     * @returns {MAP}
     * @description Getter for the modals property
     * @memberof BCMSModalState
     */
    get modals() { return this._modals; }

    /**
     * @function open
     * @instance
     * @param {STRING} id - The id of the modal to open
     * @param {*} data - The data to be passed to the modal on open
     * @returns {bcmsModalState}
     * @description Open the modal passing data and set the overlay to be shown
     * @memberof BCMSModalState
     */
    open(id, data) {
        if(this.modals.get(id) === undefined) {
            throw new Error(`Modal with the id of ${ id } does not exist within the current context`);
        }

        this.modals.get(id).open(data);
        this.isActive = true;

        return this;
    }

    /**
     * @function close
     * @instance
     * @param {STRING} id - The id of the modal to be closed
     * @returns {bcmsModalState}
     * @description Close the modal based on id, and if all modals are closed remove the overlay
     * @memberof BCMSModalState
     */
    close(id) {
        if(this.modals.get(id) === undefined) {
            throw new Error(`Modal with the id of ${ id } does not exist within the current context`);
        }

        this.modals.get(id).close();

        if(document.querySelectorAll(".bcms-modal").length === 1) {
            this.isActive = false;
        }

        return this;
    }
}